(() => {
  const CONTACT_THANKS_PATH = '/contact/thanks/';
  const CONTACT_PAGE_PATH = '/contact/';
  const RECRUIT_APPLY_PATH_PATTERN = /\/apply\/$/;
  const MOBILE_NAV_MEDIA_QUERY = '(max-width: 900px)';
  const ANCHOR_LINK_SELECTOR = 'a[href*="#"]';

  const headerRoot = document.querySelector('.site-header');
  const menuRoot = headerRoot?.querySelector('.site-header__nav');
  const menuToggle = menuRoot?.querySelector('.site-header__toggle');
  const menuDrawer = headerRoot?.querySelector('.site-header__drawer');
  const menuLinks = menuDrawer?.querySelectorAll('.site-header__nav-link') ?? [];
  const mobileMediaQuery = window.matchMedia(MOBILE_NAV_MEDIA_QUERY);
  let menuAnimationFrameId = null;

  /**
   * 外部サイトへの遷移であることを視覚的に伝えるため、テキストリンクにだけ識別クラスを付与する。
   */
  const markExternalLinks = () => {
    const links = document.querySelectorAll('a[href]');

    links.forEach((linkElement) => {
      if (!(linkElement instanceof HTMLAnchorElement)) {
        return;
      }

      if (linkElement.closest('.wp-block-button')) {
        return;
      }

      if (linkElement.querySelector('img, svg, picture')) {
        return;
      }

      const href = linkElement.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }

      let linkUrl;

      try {
        linkUrl = new URL(linkElement.href, window.location.href);
      } catch (error) {
        return;
      }

      if (!['http:', 'https:'].includes(linkUrl.protocol)) {
        return;
      }

      linkElement.classList.toggle('is-external-link', linkUrl.origin !== window.location.origin);
    });
  };

  /**
   * プライバシー同意前の誤送信を防ぐため、acceptance 未チェック時は送信を無効化する。
   */
  const syncContactAcceptanceState = () => {
    const contactForms = document.querySelectorAll('.wpcf7 form');

    contactForms.forEach((formElement) => {
      const acceptanceCheckbox = formElement.querySelector('.wpcf7-acceptance input[type="checkbox"]');
      const submitButton = formElement.querySelector('.wpcf7-submit, #submitbtn');

      if (!(acceptanceCheckbox instanceof HTMLInputElement) || !(submitButton instanceof HTMLInputElement)) {
        return;
      }

      const updateSubmitState = () => {
        submitButton.disabled = !acceptanceCheckbox.checked;
        submitButton.setAttribute('aria-disabled', String(submitButton.disabled));
      };

      if (formElement.dataset.acceptanceBound === 'true') {
        updateSubmitState();
        return;
      }

      acceptanceCheckbox.addEventListener('change', updateSubmitState);
      formElement.dataset.acceptanceBound = 'true';
      updateSubmitState();
    });
  };

  /**
   * 固定ヘッダーに隠れないよう、アンカー移動時はヘッダー高さを差し引いて滑らかに移動する。
   */
  const scrollToAnchorTarget = (targetId) => {
    if (!targetId) {
      return false;
    }

    const targetElement = document.getElementById(targetId);

    if (!targetElement) {
      return false;
    }

    const headerHeight = headerRoot?.getBoundingClientRect().height ?? 0;
    const targetTop = window.scrollY + targetElement.getBoundingClientRect().top - headerHeight;

    window.scrollTo({
      top: Math.max(targetTop, 0),
      behavior: 'smooth',
    });

    return true;
  };

  /**
   * コア navigation を外したため、テーマ側で開閉状態とスクロールロックを管理する。
   */
  const setMenuOpen = (isOpen) => {
    if (!menuToggle || !menuDrawer) {
      return;
    }

    if (menuAnimationFrameId) {
      window.cancelAnimationFrame(menuAnimationFrameId);
      menuAnimationFrameId = null;
    }

    menuToggle.setAttribute('aria-expanded', String(isOpen));
    menuToggle.setAttribute('aria-label', isOpen ? 'メニューを閉じる' : 'メニューを開く');
    document.documentElement.classList.toggle('is-site-menu-open', isOpen);
    document.body.classList.toggle('is-site-menu-open', isOpen);

    if (isOpen) {
      menuDrawer.hidden = false;
      menuDrawer.classList.add('is-animating');
      menuAnimationFrameId = window.requestAnimationFrame(() => {
        menuDrawer.classList.add('is-open');
        menuAnimationFrameId = null;
      });

      return;
    }

    menuDrawer.classList.add('is-animating');
    menuDrawer.classList.remove('is-open');
  };

  if (menuToggle && menuDrawer) {
    menuDrawer.addEventListener('transitionend', (event) => {
      if (event.target !== menuDrawer || event.propertyName !== 'opacity') {
        return;
      }

      menuDrawer.classList.remove('is-animating');

      if (menuDrawer.classList.contains('is-open')) {
        return;
      }

      menuDrawer.hidden = true;
    });

    menuToggle.addEventListener('click', () => {
      const isOpen = menuToggle.getAttribute('aria-expanded') === 'true';

      setMenuOpen(!isOpen);
    });

    menuDrawer.addEventListener('click', (event) => {
      if (event.target !== menuDrawer) {
        return;
      }

      setMenuOpen(false);
    });

    menuLinks.forEach((menuLink) => {
      menuLink.addEventListener('click', () => {
        setMenuOpen(false);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') {
        return;
      }

      setMenuOpen(false);
    });

    mobileMediaQuery.addEventListener('change', (event) => {
      if (event.matches) {
        return;
      }

      setMenuOpen(false);
    });

    setMenuOpen(false);
  }

  document.addEventListener('click', (event) => {
    const anchorLink = event.target instanceof Element ? event.target.closest(ANCHOR_LINK_SELECTOR) : null;

    if (!(anchorLink instanceof HTMLAnchorElement)) {
      return;
    }

    const url = new URL(anchorLink.href, window.location.href);
    const targetId = url.hash.replace(/^#/, '');

    if (!targetId || url.pathname !== window.location.pathname || url.origin !== window.location.origin) {
      return;
    }

    if (!scrollToAnchorTarget(targetId)) {
      return;
    }

    event.preventDefault();
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${targetId}`);
    setMenuOpen(false);
  });

  window.addEventListener('load', () => {
    const targetId = window.location.hash.replace(/^#/, '');

    if (!targetId) {
      return;
    }

    window.requestAnimationFrame(() => {
      scrollToAnchorTarget(targetId);
    });
  });

  markExternalLinks();
  syncContactAcceptanceState();
  document.addEventListener('wpcf7submit', markExternalLinks);
  document.addEventListener('wpcf7invalid', syncContactAcceptanceState);
  document.addEventListener('wpcf7reset', syncContactAcceptanceState);
  document.addEventListener('wpcf7submit', syncContactAcceptanceState);

  /**
   * お問い合わせページでの送信成功時のみサンキューページへ遷移する。
   * ページパスで絞り、他ページのCF7送信には影響させないため。
   */
  document.addEventListener('wpcf7mailsent', () => {
    const isContactPage = window.location.pathname === CONTACT_PAGE_PATH;
    const isRecruitApplyPage = RECRUIT_APPLY_PATH_PATTERN.test(window.location.pathname);

    if (!isContactPage) {
      if (!isRecruitApplyPage) {
        return;
      }

      /**
       * 採用応募フォームは配置先の親ページに依存するため、
       * 現在の apply パスを保ったまま apply-thanks へ置き換えて遷移する。
       */
      window.location.href = window.location.pathname.replace(RECRUIT_APPLY_PATH_PATTERN, '/apply-thanks/');
      return;
    }

    window.location.href = CONTACT_THANKS_PATH;
  });
})();
